"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Target, Search, MapPin, TrendingUp, CalendarCheck, ShieldCheck, ArrowRight, Stethoscope } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const coreObjectives = [{
  id: "obj-1",
  title: "High-Intent Clinical Keyword Dominance",
  metric: "Top 3 Rankings",
  description: "Capture transactional patient queries for specialised procedures including mole checks, acne scarring laser therapy, and private dermatologist consultations.",
  icon: Search
}, {
  id: "obj-2",
  title: "Regional Local Pack & Map Visibility",
  metric: "Local Pack #1",
  description: "Establish absolute authority across local search perimeters, optimising clinic profiles for nearby patients looking for urgent and elective clinical care.",
  icon: MapPin
}, {
  id: "obj-3",
  title: "Qualified Patient Appointment Uplift",
  metric: "Direct Consultations",
  description: "Transform generic organic visits into pre-qualified private patient bookings through targeted landing page structure and clear patient action paths.",
  icon: CalendarCheck
}, {
  id: "obj-4",
  title: "Medical E-E-A-T & Trust Signals",
  metric: "GMC & CQC Compliant",
  description: "Structure clinical doctor profiles, editorial references, and treatment safety guides to fulfill Google medical Search Quality Evaluator standards.",
  icon: ShieldCheck
}];
const projectMilestones = [{
  phase: "Phase 01",
  name: "Medical SEO & Clinical Keyword Audit",
  detail: "Comprehensive diagnostic of indexing blocks, search intent gaps, and regional patient search queries."
}, {
  phase: "Phase 02",
  name: "Doctor Bio & Clinical Page Architecture",
  detail: "Rebuilding procedure hubs with schema, practitioner credentials, and compliant patient treatment guidance."
}, {
  phase: "Phase 03",
  name: "Hyper-Local Citation & Entity Authority",
  detail: "Consolidating Google Business Profiles, medical registries, and clinical reviews to conquer map listings."
}];
export default function DermatologyGoal() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        duration: 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="dermatologycasestudygoal" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl mb-14 md:mb-18">
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              <Stethoscope className="size-3.5 text-primary mr-1" />
              Private Healthcare SEO Case Study
            </Badge>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default">
            The Strategic Goal: Establishing Local Search Authority for a Private Dermatology Clinic
          </h2>

          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            When this private dermatology practice partnered directly with me, their specialist treatments were obscured by generic medical directories and hospital trust portals. My goal was clear: establish dominant organic visibility for private consultations and attract self-pay patients looking for high-trust clinical expertise.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
          <motion.div className="lg:col-span-7" initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.6
        }}>
            <Card className="border border-border shadow-md bg-card">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                    <Target className="size-4 text-primary" />
                    <span>Project Brief &amp; Focus</span>
                  </div>
                  <span className="text-xs font-medium px-2.5 py-1 bg-secondary text-secondary-foreground rounded-md">
                    Technical Audit &amp; Ongoing Monitoring
                  </span>
                </div>
                <CardTitle className="text-2xl font-bold mt-2 font-default">
                  Core Mission: Attract Self-Pay Clinical Patients
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground mt-1">
                  Targeting high-intent medical consultations rather than low-value informational traffic.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <p className="text-sm sm:text-base text-foreground/90 leading-relaxed">
                  The client required a bespoke search strategy tailored specifically to medical regulatory standards. I focused on building authoritative topical depth around specialist private treatments, eliminating technical indexing issues, and positioning the clinic directly in front of patients actively searching for local consultant dermatologists.
                </p>

                <Separator />

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground font-default">
                    Implementation Roadmap
                  </h3>
                  <div className="space-y-3">
                    {projectMilestones.map((milestone, i) => <div key={milestone.phase} data-index={i} className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4 p-3 rounded-lg bg-background border border-border/80">
                        <span className="text-xs font-mono font-bold text-primary bg-secondary px-2 py-0.5 rounded-sm w-fit shrink-0">
                          {milestone.phase}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{milestone.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{milestone.detail}</p>
                        </div>
                      </div>)}
                  </div>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <Button asChild size="lg" className="w-full sm:w-auto">
                    <Link to="/ContactPage">
                      Book a Free SEO Consultation
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                    <Link to="/CaseStudiesPage">
                      Explore All Case Studies
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <div className="lg:col-span-5">
            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
            once: true,
            amount: 0.1
          }} className="grid grid-cols-1 gap-4">
              {coreObjectives.map((obj, i) => {
              const IconComponent = obj.icon;
              return <motion.div key={obj.id} data-index={i} variants={itemVariants}>
                    <Card className="border border-border/80 shadow-xs hover:shadow-md transition-[box-shadow,border-color] bg-card p-5">
                      <div className="flex items-start gap-4">
                        <div className="p-2.5 rounded-lg bg-secondary text-primary shrink-0 mt-0.5">
                          <IconComponent className="size-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <h4 className="text-base font-semibold text-foreground font-default">
                              {obj.title}
                            </h4>
                            <span className="text-xs font-mono font-semibold text-accent-foreground bg-accent px-2 py-0.5 rounded-sm shrink-0">
                              {obj.metric}
                            </span>
                          </div>
                          <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 leading-relaxed">
                            {obj.description}
                          </p>
                        </div>
                      </div>
                    </Card>
                  </motion.div>;
            })}
            </motion.div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-secondary/40 p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1 max-w-2xl">
            <div className="flex items-center gap-2">
              <TrendingUp className="size-5 text-primary shrink-0" />
              <h3 className="text-lg font-bold text-foreground font-default">
                Direct Collaboration, Measurable Patient Acquisition
              </h3>
            </div>
            <p className="text-sm text-muted-foreground">
              From preliminary technical crawl diagnostics and indexing fixes to continuous rank tracking and search visibility monitoring, the entire optimization process is methodically executed and maintained.
            </p>
          </div>
          <div className="shrink-0 w-full md:w-auto">
            <Button asChild variant="secondary" className="w-full md:w-auto border border-border">
              <Link to="/ServicesPage">
                View SEO Consultancy Services
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}